import { ref, computed } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { useToaster, EXPERT_EXPORT_FIELDS, type ExpertExportField } from '@shopexperts/shared';
import { api } from '@/services/api.service';

export interface UseExpertsExportOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Composable for exporting experts to CSV.
 * Handles field selection, export request, and file download.
 */
export function useExpertsExport(options: UseExpertsExportOptions = {}) {
  const toaster = useToaster();
  
  // State for field selection
  const selectedFields = ref<Set<ExpertExportField>>(new Set());
  
  // Get available fields with labels
  const availableFields = computed(() => {
    return Object.entries(EXPERT_EXPORT_FIELDS).map(([value, label]) => ({
      value: value as ExpertExportField,
      label,
    }));
  });
  
  // Check if any fields are selected
  const hasSelectedFields = computed(() => selectedFields.value.size > 0);
  
  // Toggle a field selection
  function toggleField(field: ExpertExportField) {
    if (selectedFields.value.has(field)) {
      selectedFields.value.delete(field);
    } else {
      selectedFields.value.add(field);
    }
    // Trigger reactivity
    selectedFields.value = new Set(selectedFields.value);
  }
  
  // Select all fields
  function selectAllFields() {
    selectedFields.value = new Set(Object.keys(EXPERT_EXPORT_FIELDS) as ExpertExportField[]);
  }
  
  // Clear all field selections
  function clearAllFields() {
    selectedFields.value = new Set();
  }
  
  // Check if a field is selected
  function isFieldSelected(field: ExpertExportField) {
    return selectedFields.value.has(field);
  }
  
  // Export mutation
  const {
    mutateAsync: exportExperts,
    isPending: isExporting,
    error,
  } = useMutation({
    mutationFn: async (fields: ExpertExportField[]) => {
      if (fields.length === 0) {
        throw new Error('Please select at least one field to export');
      }
      
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const url = `${baseUrl}/api/v2/experimental/admin/experts/export?fields=${fields.join(',')}`;
      
      // Use axios directly for blob response
      const response = await api.get(url, {
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv',
        },
      });
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `experts-export-${new Date().toISOString().slice(0, 10)}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      return {
        blob: response.data as Blob,
        filename,
      };
    },
    onSuccess: (result) => {
      // Trigger file download
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toaster.create({
        title: 'Export successful',
        description: `Downloaded ${result.filename}`,
        type: 'success',
      });
      
      options.onSuccess?.();
    },
    onError: (err) => {
      const error = err as Error;
      toaster.create({
        title: 'Export failed',
        description: error.message || 'An error occurred during export',
        type: 'error',
      });
      
      options.onError?.(error);
    },
  });
  
  // Start export with selected fields
  async function startExport() {
    const fields = Array.from(selectedFields.value);
    return exportExperts(fields);
  }
  
  return {
    // State
    selectedFields,
    availableFields,
    hasSelectedFields,
    isExporting,
    error,
    
    // Methods
    toggleField,
    selectAllFields,
    clearAllFields,
    isFieldSelected,
    startExport,
  };
}

export default useExpertsExport;
